/**
 * MajorIncidentsPage - Critical Incident Management
 * 
 * Features:
 * - List all major incidents with filtering
 * - Incident categorization and severity tracking
 * - Status workflow (Draft → Submitted → Reviewed → Closed)
 * - Regulator notification tracking
 * - Root cause analysis
 */
import React, { useState, useEffect } from 'react';
import api, { formatDate, formatDateTime } from '../lib/api';
import { INCIDENT_CATEGORIES } from '../lib/constants';
import { Card, Spinner, EmptyState, Select } from '../components/shared';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { AlertOctagon, Plus, MapPin, Calendar, FileText, X, Edit2, Send, CheckCircle, Clock, AlertTriangle } from 'lucide-react';

// Status configuration
const INCIDENT_STATUS_CONFIG = {
  DRAFT: { label: 'Draft', color: '#6b7280', bg: '#f3f4f6', icon: FileText },
  SUBMITTED: { label: 'Submitted', color: '#3b82f6', bg: '#eff6ff', icon: Send },
  UNDER_INVESTIGATION: { label: 'Investigating', color: '#f59e0b', bg: '#fffbeb', icon: Clock },
  REVIEWED: { label: 'Reviewed', color: '#8b5cf6', bg: '#f5f3ff', icon: CheckCircle },
  CLOSED: { label: 'Closed', color: '#10b981', bg: '#ecfdf5', icon: CheckCircle },
};

// Status badge
const IncidentStatusBadge = ({ status }) => {
  const config = INCIDENT_STATUS_CONFIG[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span className="status-badge" style={{ color: config.color, backgroundColor: config.bg }} data-testid={`incident-status-${status}`}>
      {config.label}
    </span>
  );
};

// Category badge
const CategoryBadge = ({ category }) => {
  const config = INCIDENT_CATEGORIES[category] || { label: category, color: '#6b7280' };
  return (
    <span className="category-badge" style={{ borderColor: config.color, color: config.color }}>
      {config.icon ? <img src={config.icon} alt="" className="category-badge-icon" /> : 
       config.emoji ? <span>{config.emoji}</span> : null}
      <span>{config.label}</span>
    </span>
  );
};

// Regulator notification badge
const RegulatorBadge = ({ notified }) => {
  if (notified) {
    return <span className="regulator-badge regulator-notified" data-testid="regulator-notified">Notified</span>;
  }
  return <span className="regulator-badge regulator-pending" data-testid="regulator-pending">Not Notified</span>;
};

// Add/Edit Modal
const IncidentFormModal = ({ isOpen, onClose, onSave, item, sites }) => {
  const [form, setForm] = useState({
    site_id: '',
    occurred_at: '',
    location_text: '',
    category: 'INJURY',
    summary: '',
    immediate_actions: '',
    injuries_description: '',
    property_damage: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        site_id: item.site_id || '',
        occurred_at: item.occurred_at ? new Date(item.occurred_at).toISOString().slice(0, 16) : '',
        location_text: item.location_text || '',
        category: item.category || 'INJURY',
        summary: item.summary || '',
        immediate_actions: item.immediate_actions || '',
        injuries_description: item.injuries_description || '',
        property_damage: item.property_damage || '',
      });
    } else {
      const now = new Date();
      setForm({
        site_id: sites[0]?.id || '',
        occurred_at: now.toISOString().slice(0, 16),
        location_text: '',
        category: 'INJURY',
        summary: '',
        immediate_actions: '',
        injuries_description: '',
        property_damage: '',
      });
    }
  }, [item, sites]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.summary || !form.site_id || !form.occurred_at) {
      toast.error('Please fill in required fields');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" data-testid="incident-form-modal">
      <div className="modal-content modal-lg">
        <div className="modal-header">
          <h2>{item ? 'Edit Incident' : 'Report Major Incident'}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label required">Site</label>
                <select 
                  className="form-select" 
                  value={form.site_id} 
                  onChange={(e) => setForm({...form, site_id: e.target.value})}
                  data-testid="incident-site-select"
                >
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label required">Date/Time Occurred</label>
                <input 
                  type="datetime-local" 
                  className="form-input" 
                  value={form.occurred_at}
                  onChange={(e) => setForm({...form, occurred_at: e.target.value})}
                  data-testid="incident-datetime"
                />
              </div>
              <div className="form-group">
                <label className="form-label required">Category</label>
                <select 
                  className="form-select" 
                  value={form.category}
                  onChange={(e) => setForm({...form, category: e.target.value})}
                  data-testid="incident-category-select"
                >
                  {Object.entries(INCIDENT_CATEGORIES).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Specific Location</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="e.g. Loading bay area"
                  value={form.location_text}
                  onChange={(e) => setForm({...form, location_text: e.target.value})}
                  data-testid="incident-location"
                />
              </div>
              <div className="form-group form-group-full">
                <label className="form-label required">Summary</label>
                <textarea 
                  className="form-textarea" 
                  rows={3}
                  placeholder="Describe what happened..."
                  value={form.summary}
                  onChange={(e) => setForm({...form, summary: e.target.value})}
                  data-testid="incident-summary"
                />
              </div>
              <div className="form-group form-group-full">
                <label className="form-label">Immediate Actions Taken</label>
                <textarea 
                  className="form-textarea" 
                  rows={2}
                  placeholder="What was done immediately after the incident?"
                  value={form.immediate_actions}
                  onChange={(e) => setForm({...form, immediate_actions: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Injuries Description</label>
                <textarea 
                  className="form-textarea" 
                  rows={2}
                  placeholder="Describe any injuries sustained"
                  value={form.injuries_description}
                  onChange={(e) => setForm({...form, injuries_description: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Property Damage</label>
                <textarea 
                  className="form-textarea" 
                  rows={2}
                  placeholder="Describe any property damage"
                  value={form.property_damage}
                  onChange={(e) => setForm({...form, property_damage: e.target.value})}
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving} data-testid="incident-save-btn">
              {saving ? 'Saving...' : (item ? 'Update' : 'Report Incident')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Detail Modal
const IncidentDetailModal = ({ isOpen, onClose, item, onEdit, onUpdateStatus, onNotifyRegulator }) => {
  if (!isOpen || !item) return null;

  const canSubmit = item.status === 'DRAFT';
  const canInvestigate = item.status === 'SUBMITTED';
  const canReview = item.status === 'UNDER_INVESTIGATION';
  const canClose = item.status === 'REVIEWED';

  return (
    <div className="modal-overlay" data-testid="incident-detail-modal">
      <div className="modal-content modal-lg">
        <div className="modal-header">
          <div className="modal-header-content">
            <CategoryBadge category={item.category} />
            <IncidentStatusBadge status={item.status} />
          </div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <h2 className="incident-title">{item.summary}</h2>
          
          <div className="detail-grid">
            <div className="detail-section">
              <h3>When & Where</h3>
              <div className="detail-row">
                <span className="detail-label">Date/Time</span>
                <span className="detail-value">{formatDateTime(item.occurred_at)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Site</span>
                <span className="detail-value">{item.site_name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Location</span>
                <span className="detail-value">{item.location_text || 'Not specified'}</span>
              </div>
            </div>
            
            <div className="detail-section">
              <h3>Regulatory</h3>
              <div className="detail-row">
                <span className="detail-label">Regulator Notified</span>
                <RegulatorBadge notified={item.regulator_notified} />
              </div>
              {item.regulator_notified && item.regulator_notification_date && (
                <div className="detail-row">
                  <span className="detail-label">Notification Date</span>
                  <span className="detail-value">{formatDate(item.regulator_notification_date)}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Reported By</span>
                <span className="detail-value">{item.created_by_name}</span>
              </div>
            </div>

            {item.immediate_actions && (
              <div className="detail-section detail-section-full">
                <h3>Immediate Actions</h3>
                <p className="detail-text">{item.immediate_actions}</p>
              </div>
            )}

            {item.injuries_description && (
              <div className="detail-section">
                <h3>Injuries</h3>
                <p className="detail-text">{item.injuries_description}</p>
              </div>
            )}

            {item.property_damage && (
              <div className="detail-section">
                <h3>Property Damage</h3>
                <p className="detail-text">{item.property_damage}</p>
              </div>
            )}

            {item.root_cause_analysis && (
              <div className="detail-section detail-section-full">
                <h3>Root Cause Analysis</h3>
                <p className="detail-text">{item.root_cause_analysis}</p>
              </div>
            )}
          </div>

          {/* Status Actions */}
          {(canSubmit || canInvestigate || canReview || canClose) && (
            <div className="status-actions">
              <h3>Workflow Actions</h3>
              <div className="status-buttons">
                {canSubmit && (
                  <Button variant="primary" onClick={() => onUpdateStatus(item, 'SUBMITTED')} data-testid="submit-incident">
                    <Send size={16} /> Submit for Review
                  </Button>
                )}
                {canInvestigate && (
                  <Button variant="primary" onClick={() => onUpdateStatus(item, 'UNDER_INVESTIGATION')}>
                    <Clock size={16} /> Begin Investigation
                  </Button>
                )}
                {canReview && (
                  <Button variant="primary" onClick={() => onUpdateStatus(item, 'REVIEWED')}>
                    <CheckCircle size={16} /> Mark Reviewed
                  </Button>
                )}
                {canClose && (
                  <Button variant="success" onClick={() => onUpdateStatus(item, 'CLOSED')} data-testid="close-incident">
                    <CheckCircle size={16} /> Close Incident
                  </Button>
                )}
                {!item.regulator_notified && item.status !== 'DRAFT' && (
                  <Button variant="outline" onClick={() => onNotifyRegulator(item)} data-testid="notify-regulator">
                    <AlertTriangle size={16} /> Mark Regulator Notified
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button variant="primary" onClick={() => onEdit(item)} data-testid="incident-edit-btn">
            <Edit2 size={16} /> Edit
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Page Component
const MajorIncidentsPage = () => {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ site_id: '', status: '', category: '' });
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load sites
      const sitesRes = await api.get('/sites');
      setSites(sitesRes.items || []);

      // Load incidents
      let url = '/major-incidents';
      const params = new URLSearchParams();
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.status) params.append('status', filters.status);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await api.get(url);
      let incidents = res.items || [];
      
      // Filter by category on frontend
      if (filters.category) {
        incidents = incidents.filter(i => i.category === filters.category);
      }
      
      setItems(incidents);
    } catch (err) {
      console.error('Failed to load incidents:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    if (selectedItem) {
      await api.patch(`/major-incidents/${selectedItem.id}`, formData);
      toast.success('Incident updated');
    } else {
      await api.post('/major-incidents', formData);
      toast.success('Incident reported');
    }
    loadData();
    setSelectedItem(null);
  };

  const handleUpdateStatus = async (item, newStatus) => {
    try {
      await api.patch(`/major-incidents/${item.id}`, { status: newStatus });
      toast.success(`Status updated to ${INCIDENT_STATUS_CONFIG[newStatus]?.label || newStatus}`);
      setShowDetailModal(false);
      loadData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleNotifyRegulator = async (item) => {
    try {
      await api.patch(`/major-incidents/${item.id}`, { 
        regulator_notified: true,
        regulator_notification_date: new Date().toISOString()
      });
      toast.success('Regulator notification recorded');
      setShowDetailModal(false);
      loadData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setShowFormModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowDetailModal(false);
    setShowFormModal(true);
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  // Calculate summary stats
  const stats = {
    total: items.length,
    open: items.filter(i => !['CLOSED'].includes(i.status)).length,
    thisMonth: items.filter(i => {
      const occurred = new Date(i.occurred_at);
      const now = new Date();
      return occurred.getMonth() === now.getMonth() && occurred.getFullYear() === now.getFullYear();
    }).length,
    pendingNotification: items.filter(i => !i.regulator_notified && i.status !== 'DRAFT').length,
  };

  return (
    <div className="page incidents-page" data-testid="incidents-page">
      <div className="page-header">
        <div>
          <h1>Major Incidents</h1>
          <p>Track critical incidents, investigations, and regulatory notifications</p>
        </div>
        <Button variant="primary" onClick={handleAdd} data-testid="add-incident-btn">
          <Plus size={16} /> Report Incident
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="page-stats" data-testid="incidents-stats">
        <div className="stat-card">
          <AlertOctagon size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
        </div>
        <div className={`stat-card ${stats.open > 0 ? 'stat-warning' : ''}`}>
          <Clock size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.open}</span>
            <span className="stat-label">Open</span>
          </div>
        </div>
        <div className="stat-card">
          <Calendar size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.thisMonth}</span>
            <span className="stat-label">This Month</span>
          </div>
        </div>
        <div className={`stat-card ${stats.pendingNotification > 0 ? 'stat-danger' : ''}`}>
          <AlertTriangle size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.pendingNotification}</span>
            <span className="stat-label">Pending Notification</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <div className="filters-row">
          <Select
            label="Site"
            value={filters.site_id}
            onChange={(e) => setFilters({ ...filters, site_id: e.target.value })}
            options={[
              { value: '', label: 'All Sites' },
              ...sites.map(s => ({ value: s.id, label: s.name }))
            ]}
          />
          <Select
            label="Status"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            options={[
              { value: '', label: 'All Statuses' },
              ...Object.entries(INCIDENT_STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))
            ]}
          />
          <Select
            label="Category"
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            options={[
              { value: '', label: 'All Categories' },
              ...Object.entries(INCIDENT_CATEGORIES).map(([k, v]) => ({ value: k, label: v.label }))
            ]}
          />
          {(filters.site_id || filters.status || filters.category) && (
            <button className="filter-clear-btn" onClick={() => setFilters({ site_id: '', status: '', category: '' })}>
              Clear filters
            </button>
          )}
        </div>
      </Card>

      {/* Items List */}
      {loading ? (
        <div className="page-loading"><Spinner /></div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<AlertOctagon size={48} />}
          title="No incidents found"
          description={filters.site_id || filters.status || filters.category ? "No incidents match the current filters" : "No major incidents have been reported"}
        />
      ) : (
        <Card className="table-card">
          <table className="data-table" data-testid="incidents-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Category</th>
                <th>Summary</th>
                <th>Site</th>
                <th>Status</th>
                <th>Regulator</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr 
                  key={item.id} 
                  onClick={() => handleView(item)}
                  className="table-row clickable"
                  data-testid={`incident-row-${item.id}`}
                >
                  <td className="cell-date">{formatDate(item.occurred_at)}</td>
                  <td><CategoryBadge category={item.category} /></td>
                  <td className="cell-title cell-truncate">{item.summary}</td>
                  <td>{item.site_name}</td>
                  <td><IncidentStatusBadge status={item.status} /></td>
                  <td><RegulatorBadge notified={item.regulator_notified} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modals */}
      <IncidentFormModal
        isOpen={showFormModal}
        onClose={() => { setShowFormModal(false); setSelectedItem(null); }}
        onSave={handleSave}
        item={selectedItem}
        sites={sites}
      />
      <IncidentDetailModal
        isOpen={showDetailModal}
        onClose={() => { setShowDetailModal(false); setSelectedItem(null); }}
        item={selectedItem}
        onEdit={handleEdit}
        onUpdateStatus={handleUpdateStatus}
        onNotifyRegulator={handleNotifyRegulator}
      />
    </div>
  );
};

export default MajorIncidentsPage;
